/**
 * HistoricalChart - Electoral Trend Visualization with Year Slider
 *
 * Displays historical electoral data using Plotly.js with:
 * - Animated bar chart with year slider
 * - Line chart for trend analysis
 *
 * Author: Claude Code
 * Date: 2025-12-01
 */

import React, { useEffect, useState, useRef } from 'react';
import Plot from 'react-plotly.js';

interface HistoricalChartProps {
  municipalityCode: string;
  electionType?: string;
  chartType?: 'animation' | 'lines';
  topN?: number;
}

interface PlotlyData {
  data: any[];
  layout: any;
  frames?: any[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const HistoricalChart: React.FC<HistoricalChartProps> = ({
  municipalityCode,
  electionType = 'komunalni',
  chartType = 'lines',
  topN = 8
}) => {
  const [chartData, setChartData] = useState<PlotlyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const plotRef = useRef<any>(null);

  // Fetch chart data when params change
  useEffect(() => {
    const fetchChartData = async () => {
      if (!municipalityCode) return;

      setLoading(true);
      setError(null);

      try {
        const url = `${API_BASE_URL}/api/analysis/historical/chart/${municipalityCode}?election_type=${electionType}&chart_type=${chartType}&top_n=${topN}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        setChartData(data);
      } catch (err: any) {
        console.error('Chart data fetch error:', err);
        setError(err.message || 'Failed to load chart data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [municipalityCode, electionType, chartType, topN]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-gray-600">Načítám historická data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg">
        <div className="text-center">
          <svg className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <p className="text-gray-500">Vyberte obec pro zobrazení historických dat</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <Plot
        ref={plotRef}
        data={chartData.data}
        layout={{
          ...chartData.layout,
          autosize: true,
          margin: { t: 80, r: 30, b: 80, l: 60 },
          font: { family: 'Arial, sans-serif' }
        }}
        frames={chartData.frames}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
          toImageButtonOptions: {
            format: 'png',
            filename: `electoral_trends_${municipalityCode}`,
            height: 600,
            width: 1000,
            scale: 2
          }
        }}
        style={{ width: '100%', height: '500px' }}
      />
    </div>
  );
};

export default HistoricalChart;
